// The words of /welcome (components/onboarding/) and of the rules that write its sentences
// (domain/welcome.ts, welcomeAccounts.ts, firstImpression.ts, firstAnalysis.ts).
import { words } from "../services/i18n.ts";

const n = (value: number) => value.toLocaleString("en");
const nEs = (value: number) => value.toLocaleString("es-ES");

export const t = words({
    en: {
        // The rail
        loading: "One moment…",
        hello: (name: string) => (name ? `Welcome, ${name}` : "Welcome"),
        intro: "Five steps and the site is about your chess, not anybody's.",
        where: (step: number, of: number, label: string) => `Step ${step} of ${of}: ${label}`,
        stepProviders: "Where you play",
        stepAbout: "Your chess",
        promiseAbout: "What you think of it, to check against your games",
        stepGames: "Your games",
        stepLevel: "Your sparring",
        stepResults: "Your level",
        promiseProviders: "Every account, on either site",
        promiseGames: (months: number) => `The last ${months} months, then every night`,
        promiseLevel: "Who answers your moves",
        promiseResults: "How well you really play",
        nextSee: "See what they say",
        nextMeanwhile: "Choose your sparring partner meanwhile",
        later: "Finish later",
        laterNote: "It stays on your dashboard until you do.",

        // Where you play
        providersTitle: "Where do you play?",
        providersLead: (months: number) => `Add every account you play on, on either site: plenty of people have more than one. We bring down the last ${months} months of games of each and work out which openings are costing you points. Nothing is posted anywhere and nothing is shared.`,
        lichessNote: "Your first Lichess account asks for permission once. It also lets the board answer with the moves real players of your level make.",
        connectLichess: "Connect Lichess",
        connectedAs: "Connected as",
        yourAccount: "your account",
        notHereYet: ". Its games are not here yet.",
        addItsGames: "Add its games",
        anotherLichess: "Another Lichess username",
        chesscomNote: "Just the username. Public games need no permission.",
        chesscomLabel: "chess.com username",
        anotherChesscom: "Another chess.com username",
        yourChesscom: "Your chess.com username",
        checking: "Checking…",
        add: "Add",
        changeLater: "You can add or remove accounts later, from My games.",
        atLeastOne: "Add at least one account to go on.",

        // Account rows
        waiting: "Waiting its turn",
        soFar: (games: number) => `${n(games)} games so far`,
        asking: "Asking for your games",
        reading: (games: number) => `${n(games)} games, reading them`,
        gamesCount: (games: number) => `${n(games)} games`,
        failed: "Failed. Add it again to retry",

        // Your games
        comingIn: "Your games are coming in",
        areIn: "Your games are in",
        positionOf: (name: string) => `The position of the ${name}`,
        startingPosition: "The starting position",
        justIn: "Just in",
        youPlay: "You play",
        appearHere: "Your openings appear here as the games arrive",
        noGamesYet: "No games yet",
        number: (value: number) => n(value),
        gamesFrom: (games: number, accounts: number) =>
            `${games === 1 ? "game" : "games"} from ${accounts} account${accounts === 1 ? "" : "s"}`,
        openingsSoFar: "The openings in your games so far",
        playSoFar: "What you play, so far",
        playMost: "What you play most",
        moreOpenings: (count: number) => `And ${count} more openings.`,
        leaveWhileRunning: "You do not have to wait here: the download carries on if you leave. From tonight on, the games you play come in by themselves, every night.",
        everyNight: "From tonight on, the games you play come in by themselves, every night.",

        // Your sparring
        partnerTitle: "Your sparring partner",
        lookingAtRatings: "Looking at your ratings…",
        noRating: "We could not find a rating on your accounts, so this is the level most people play at.",
        ratingSource: (provider: string, speed: string, username: string, rating: number, lichess: number | null) =>
            `Your ${provider} ${speed} rating (${username}) is ${rating}${lichess ? `, about ${lichess} on Lichess,` : ""} so this is where we put your partner.`,
        changeIt: "Change it if it is not right.",
        setForYou: "We have set it for you; change it if it is not right.",
        likeClub: "Like a club player",
        suggested: "Suggested",
        clubDetail: (range: string | null) =>
            `Plays the moves Lichess players ${range ? `rated ${range}` : "of your level"} really make, mistakes included.`,
        playersRated: "Players rated",
        ownChoice: "Your own choice",
        whatMeans: "What does this mean?",
        clubMore1: "When you practise a line, every answer comes from real games. Of all the games Lichess players of that rating played from the position on the board, the partner picks a move in proportion to how often it was played. So you meet what you will really meet, bad moves included, and learn how to punish them.",
        clubMore2: "The deeper a line goes, the fewer games reached it. When nobody at that level has played on from a position, the practice tells you so instead of making a move up.",
        likeMaster: "Like a master",
        masterDetail: "Only the moves masters chose over the board. Harder, and further from what you meet in an online game.",
        masterMore: "The answers come from the Lichess masters database: over-the-board games between players rated 2200 and up. They know the theory, so lines stay in the main roads and run deeper, and the mistakes club players make are rare in it.",
        changeInSettings: "You can change this any time in Settings.",

        // First impression
        positionOfLine: (family: string, variation: string) =>
            `The position of the ${family}${variation ? `, ${variation}` : ""}`,
        pointsAndRecord: "points out of 10",
        over: (points: string, games: number) => `${points} over ${games} games`,
        worstOpenings: "Your worst openings",
        worstStillReading: "Your worst openings, still being read",
        noneUnder: "No opening of yours scores clearly under the rest of your games with this colour.",
        bestOpening: "Your best opening",
        bestOpenings: "Your best openings",
        stillBeingRead: "Your games are still being read. As soon as they are in, this is where you see which openings are costing you points, and it will be on your dashboard too.",
        openingsFailed: "Your openings could not be read just now. They are on your dashboard.",
        readingOpenings: "Reading your openings…",
        partial: (games: number) => `Read from the ${n(games)} games in so far. The rest are still coming in, and this page updates on its own when they are.`,
        headlineNoGamesYet: "Your games with this colour are still coming in.",
        headlineNoGames: "No games with this colour in the last twelve months.",
        headlineStillReading: "Still reading your games: your worst openings show up here as more of them come in.",
        headlineNothing: "Nothing stands out as costing you points. That is a good place to be.",
        headlineStudy: (names: string[], far: boolean, level: boolean) => {
            const spoken = names.map((name) => `the ${name.replace(/^The /i, "")}`);
            const list = spoken.length <= 1 ? spoken[0] || ""
                : `${spoken.slice(0, -1).join(", ")} and ${spoken[spoken.length - 1]}`;
            return `Study ${list} first: you take ${far ? "far fewer" : "fewer"} points there than ${level ? "players of your level" : "in the rest of your games"}.`;
        },
        levelUnder: (theirs: string, yours: string) => `Players of your level take ${theirs} here. You take ${yours}.`,
        levelOver: (yours: string, theirs: string) => `You take ${yours} here, more than the ${theirs} players of your level manage.`,
        averageUnder: (yours: string, theirs: string, side: string) => `You take ${yours} here, and ${theirs} across all your games with ${side}.`,
        averageOver: (yours: string, theirs: string, side: string) => `You take ${yours} here, above your ${theirs} with ${side}.`,

        // The first analysis
        analysis: "Analysis",
        readAnalysis: "Read the analysis",
        timeLeft: (left: string) => `${left.charAt(0).toUpperCase()}${left.slice(1)} left at the pace it is reading.`,
        comingTitle: (games: number) => `Next: your last ${games} games, move by move`,
        comingText: (games: number) => `As soon as your games are in, Stockfish starts reading your last ${games}: the move that lost each one, the kind of mistake it was, and whether the clock had a part. We will email you the report.`,
        runningTitle: (games: number) => `Stockfish is reading your last ${games} games`,
        queuedText: "It starts in a moment. You can leave: the report comes to your inbox when it is done.",
        runningText: (left: string | null) => `${left ?? "Move by move, a few minutes in all."} You can leave: the report comes to your inbox when it is done.`,
        readyTitle: "Your report is ready",
        doneText: (games: number) => `${games} games read move by move: why you lose, the kind of mistake, the clock and the openings where it keeps happening. It is in your inbox too.`,
        failedTitle: "The analysis could not finish",
        failedText: "Nothing was charged for what it did not read. Ask for it again from the report.",
        trialNote: (day: string) => `Your first month is free, until ${day}: from now on every game you play is analysed a few minutes after it ends.`,
        stripRunning: (left: string | null) => `Move by move: why you lose, the kind of mistake and the clock. ${left ? `${left} ` : ""}The report comes to your inbox when it is done.`,
        seeHowItGoes: "See how it goes",
        stripReady: (games: number) => `${games} games read move by move. The move that lost each one, and the openings where it keeps happening.`,
        readReport: "Read the report",

        // The invitation on the dashboard
        inviteConnectTitle: "Connect your games",
        inviteConnectLead: "Tell us where you play and everything here starts being about your games: why you lose them, which openings cost you points, and what to train today.",
        inviteConnectAction: "Connect my games",
        inviteNoGamesTitle: "No games of yours yet",
        inviteNoGamesLead: "Your account is connected but none of your games came in. Bring them in and the analysis and your openings start from them.",
        inviteNoGamesAction: "Bring my games in",
        inviteRunningTitle: "Your games are coming in",
        inviteRunningLead: "Choose who answers your moves while they download, and see what they say once they are in.",
        inviteFinish: "Finish setting up",
        inviteResultsTitle: "See what your games say",
        inviteResultsLead: "Your games are in. The last step of the welcome shows the openings costing you the most points.",
    },
    es: {
        loading: "Un momento…",
        hello: (name: string) => (name ? `Hola, ${name}` : "Hola"),
        intro: "Cinco pasos y la web habla de tu ajedrez, no del de cualquiera.",
        where: (step: number, of: number, label: string) => `Paso ${step} de ${of}: ${label}`,
        stepProviders: "Dónde juegas",
        stepAbout: "Tu juego",
        promiseAbout: "Lo que crees de él, para contrastarlo con tus partidas",
        stepGames: "Tus partidas",
        stepLevel: "Tu sparring",
        stepResults: "Tu nivel",
        promiseProviders: "Todas tus cuentas, en cualquiera de las dos webs",
        promiseGames: (months: number) => `Los últimos ${months} meses, y luego cada noche`,
        promiseLevel: "Quién responde a tus jugadas",
        promiseResults: "Cuánto juegas de verdad",
        nextSee: "Ver qué dicen",
        nextMeanwhile: "Elige tu sparring mientras tanto",
        later: "Terminar más tarde",
        laterNote: "Seguirá en tu inicio hasta que lo termines.",

        providersTitle: "¿Dónde juegas?",
        providersLead: (months: number) => `Añade todas las cuentas en las que juegas, en cualquiera de las dos webs: mucha gente tiene más de una. Traemos los últimos ${months} meses de partidas de cada una y calculamos qué aperturas te están costando puntos. No se publica nada ni se comparte nada.`,
        lichessNote: "Tu primera cuenta de Lichess pide permiso una sola vez. También permite que el tablero responda con las jugadas que hacen jugadores reales de tu nivel.",
        connectLichess: "Conectar Lichess",
        connectedAs: "Cuenta conectada:",
        yourAccount: "tu cuenta",
        notHereYet: ". Sus partidas todavía no están aquí.",
        addItsGames: "Añadir sus partidas",
        anotherLichess: "Otro usuario de Lichess",
        chesscomNote: "Solo el nombre de usuario. Las partidas públicas no necesitan permiso.",
        chesscomLabel: "Usuario de chess.com",
        anotherChesscom: "Otro usuario de chess.com",
        yourChesscom: "Tu usuario de chess.com",
        checking: "Comprobando…",
        add: "Añadir",
        changeLater: "Puedes añadir o quitar cuentas más tarde, desde Mis partidas.",
        atLeastOne: "Añade al menos una cuenta para seguir.",

        waiting: "Esperando su turno",
        soFar: (games: number) => `${nEs(games)} partidas por ahora`,
        asking: "Pidiendo tus partidas",
        reading: (games: number) => `${nEs(games)} partidas, leyéndolas`,
        gamesCount: (games: number) => `${nEs(games)} partidas`,
        failed: "Ha fallado. Añádela de nuevo para reintentar",

        comingIn: "Tus partidas están llegando",
        areIn: "Tus partidas ya están aquí",
        positionOf: (name: string) => `Posición de ${name}`,
        startingPosition: "La posición inicial",
        justIn: "Recién llegada",
        youPlay: "Juegas",
        appearHere: "Tus aperturas aparecen aquí según llegan las partidas",
        noGamesYet: "Todavía no hay partidas",
        number: (value: number) => nEs(value),
        gamesFrom: (games: number, accounts: number) =>
            `${games === 1 ? "partida" : "partidas"} de ${accounts} cuenta${accounts === 1 ? "" : "s"}`,
        openingsSoFar: "Las aperturas de tus partidas hasta ahora",
        playSoFar: "Lo que juegas, por ahora",
        playMost: "Lo que más juegas",
        moreOpenings: (count: number) => `Y ${count} aperturas más.`,
        leaveWhileRunning: "No hace falta que esperes aquí: la descarga sigue aunque te vayas. Desde esta noche, las partidas que juegues llegan solas, cada noche.",
        everyNight: "Desde esta noche, las partidas que juegues llegan solas, cada noche.",

        partnerTitle: "Tu sparring",
        lookingAtRatings: "Mirando tus ratings…",
        noRating: "No hemos encontrado un rating en tus cuentas, así que es el nivel al que juega la mayoría.",
        ratingSource: (provider: string, speed: string, username: string, rating: number, lichess: number | null) =>
            `Tu rating ${speed} en ${provider} (${username}) es ${rating}${lichess ? `, unos ${lichess} en Lichess,` : ""} así que ahí hemos puesto a tu sparring.`,
        changeIt: "Cámbialo si no es correcto.",
        setForYou: "Lo hemos elegido por ti; cámbialo si no es correcto.",
        likeClub: "Como un jugador de club",
        suggested: "Recomendado",
        clubDetail: (range: string | null) =>
            `Juega las jugadas que hacen de verdad los jugadores de Lichess ${range ? `con ${range} de rating` : "de tu nivel"}, errores incluidos.`,
        playersRated: "Jugadores con rating",
        ownChoice: "Elección propia",
        whatMeans: "¿Qué significa esto?",
        clubMore1: "Cuando practicas una línea, cada respuesta sale de partidas reales. De todas las partidas que jugadores de Lichess con ese rating jugaron desde la posición del tablero, tu sparring elige una jugada en proporción a las veces que se jugó. Así te encuentras lo que te vas a encontrar de verdad, malas jugadas incluidas, y aprendes a castigarlas.",
        clubMore2: "Cuanto más profunda es una línea, menos partidas llegaron a ella. Cuando nadie de ese nivel ha seguido jugando desde una posición, la práctica te lo dice en lugar de inventarse una jugada.",
        likeMaster: "Como un maestro",
        masterDetail: "Solo las jugadas que eligieron los maestros sobre el tablero. Más difícil, y más lejos de lo que te encuentras en una partida online.",
        masterMore: "Las respuestas salen de la base de datos de maestros de Lichess: partidas sobre el tablero entre jugadores de 2200 o más. Se saben la teoría, así que las líneas siguen los caminos principales y llegan más hondo, y los errores de los jugadores de club apenas aparecen.",
        changeInSettings: "Puedes cambiarlo cuando quieras en Ajustes.",

        positionOfLine: (family: string, variation: string) =>
            `Posición de ${family}${variation ? `, ${variation}` : ""}`,
        pointsAndRecord: "puntos de 10",
        over: (points: string, games: number) => `${points} en ${games} partidas`,
        worstOpenings: "Tus peores aperturas",
        worstStillReading: "Tus peores aperturas, todavía leyéndose",
        noneUnder: "Ninguna de tus aperturas puntúa claramente por debajo del resto de tus partidas con este color.",
        bestOpening: "Tu mejor apertura",
        bestOpenings: "Tus mejores aperturas",
        stillBeingRead: "Todavía estamos leyendo tus partidas. En cuanto estén, aquí verás qué aperturas te están costando puntos, y también estará en tu inicio.",
        openingsFailed: "No hemos podido leer tus aperturas ahora mismo. Las tienes en tu inicio.",
        readingOpenings: "Leyendo tus aperturas…",
        partial: (games: number) => `Leído a partir de las ${nEs(games)} partidas que han llegado. El resto sigue llegando, y esta página se actualiza sola cuando estén.`,
        headlineNoGamesYet: "Tus partidas con este color todavía están llegando.",
        headlineNoGames: "No hay partidas con este color en los últimos doce meses.",
        headlineStillReading: "Seguimos leyendo tus partidas: tus peores aperturas aparecen aquí según van llegando.",
        headlineNothing: "Nada destaca como algo que te cueste puntos. Es un buen sitio en el que estar.",
        headlineStudy: (names: string[], far: boolean, level: boolean) => {
            const list = names.length <= 1 ? names[0] || ""
                : `${names.slice(0, -1).join(", ")} y ${names[names.length - 1]}`;
            return `Estudia primero ${list}: ahí sacas ${far ? "muchos menos" : "menos"} puntos que ${level ? "los jugadores de tu nivel" : "en el resto de tus partidas"}.`;
        },
        levelUnder: (theirs: string, yours: string) => `Los jugadores de tu nivel sacan aquí ${theirs}. Tú sacas ${yours}.`,
        levelOver: (yours: string, theirs: string) => `Aquí sacas ${yours}, más que los ${theirs} de los jugadores de tu nivel.`,
        averageUnder: (yours: string, theirs: string, side: string) => `Aquí sacas ${yours}, y ${theirs} en todas tus partidas con ${side}.`,
        averageOver: (yours: string, theirs: string, side: string) => `Aquí sacas ${yours}, por encima de tus ${theirs} con ${side}.`,

        analysis: "Análisis",
        readAnalysis: "Leer el análisis",
        timeLeft: (left: string) => `Quedan ${left} al ritmo al que lee.`,
        comingTitle: (games: number) => `Lo siguiente: tus últimas ${games} partidas, jugada a jugada`,
        comingText: (games: number) => `En cuanto lleguen tus partidas, Stockfish empieza a leer las últimas ${games}: la jugada que perdió cada una, qué clase de error fue y si el reloj tuvo algo que ver. Te enviaremos el informe por email.`,
        runningTitle: (games: number) => `Stockfish está leyendo tus últimas ${games} partidas`,
        queuedText: "Empieza en un momento. Puedes irte: el informe te llega al correo cuando termine.",
        runningText: (left: string | null) => `${left ?? "Jugada a jugada, unos minutos en total."} Puedes irte: el informe te llega al correo cuando termine.`,
        readyTitle: "Tu informe está listo",
        doneText: (games: number) => `${games} partidas leídas jugada a jugada: por qué pierdes, qué clase de error, el reloj y las aperturas en las que se repite. También lo tienes en tu correo.`,
        failedTitle: "El análisis no ha podido terminar",
        failedText: "No se ha cobrado nada de lo que no ha leído. Vuelve a pedirlo desde el informe.",
        trialNote: (day: string) => `Tu primer mes es gratis, hasta el ${day}: desde ahora cada partida que juegues se analiza unos minutos después de terminar.`,
        stripRunning: (left: string | null) => `Jugada a jugada: por qué pierdes, qué clase de error y el reloj. ${left ? `${left} ` : ""}El informe te llega al correo cuando termine.`,
        seeHowItGoes: "Ver cómo va",
        stripReady: (games: number) => `${games} partidas leídas jugada a jugada. La jugada que perdió cada una, y las aperturas en las que se repite.`,
        readReport: "Leer el informe",

        inviteConnectTitle: "Conecta tus partidas",
        inviteConnectLead: "Dinos dónde juegas y todo esto empieza a hablar de tus partidas: por qué las pierdes, qué aperturas te cuestan puntos y qué entrenar hoy.",
        inviteConnectAction: "Conectar mis partidas",
        inviteNoGamesTitle: "Todavía no hay partidas tuyas",
        inviteNoGamesLead: "Tu cuenta está conectada pero no ha llegado ninguna partida. Tráelas y el análisis y tus aperturas empiezan a partir de ellas.",
        inviteNoGamesAction: "Traer mis partidas",
        inviteRunningTitle: "Tus partidas están llegando",
        inviteRunningLead: "Elige quién responde a tus jugadas mientras se descargan, y mira qué dicen cuando estén.",
        inviteFinish: "Terminar la configuración",
        inviteResultsTitle: "Mira qué dicen tus partidas",
        inviteResultsLead: "Tus partidas ya están. El último paso de la bienvenida te enseña las aperturas que más puntos te cuestan.",
    },
});
